import { researchCms } from "@/api/researchCms.service";

export type OrgEntryLookup = {
  editionId: string;
  bpaId: string;
  companyUuid: string;
  rankingTableId?: string;
  /** Reopen exactly the untabled (practice-area-level) row — the row a main-table section
   * rendered. Mutually exclusive with rankingTableId; never falls back to a different table's
   * row, unlike an absent rankingTableId, which applies no scope at all. */
  untabledOnly?: boolean;
  /** Skip the deleted-row re-check, so only live entries are returned. */
  liveOnly?: boolean;
};

export type OrgEntry = { id: string; docParseResultId: string | null };

/** Finds the organisation-level entry for a firm in a practice area, as the org page does. */
export async function findOrgEntry(params: OrgEntryLookup): Promise<OrgEntry | null> {
  const filters = {
    editionId: params.editionId,
    bpaId: params.bpaId,
    companyId: params.companyUuid,
    level: "organisation" as const,
    rankingTableId: params.rankingTableId,
    rankingTableScope: params.untabledOnly ? ("untabled" as const) : undefined,
    limit: 1,
  };
  const list = await researchCms.getEditorialContentList(filters);
  const live = list.editorialContent[0];
  if (live) return { id: live.id, docParseResultId: live.docParseResultId };
  if (params.liveOnly) return null;
  const deleted = await researchCms.getEditorialContentList({ ...filters, status: "deleted" });
  const deletedRow = deleted.editorialContent[0];
  return deletedRow ? { id: deletedRow.id, docParseResultId: deletedRow.docParseResultId } : null;
}

export async function findOrgContentId(params: OrgEntryLookup): Promise<string | null> {
  const entry = await findOrgEntry(params);
  return entry?.id ?? null;
}
